import React, { useState, useEffect } from 'react';
import { Moon, Sun, Bell, BellOff, Info, MapPin, Languages, LogOut, Crown, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

// Import newly created components
import LoginPage from './components/auth/LoginPage';
import SignupPage from './components/auth/SignupPage';
import OTPVerification from './components/auth/OTPVerification';
import PricingPage from './components/pricing/PricingPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RestrictedAccess from './components/auth/RestrictedAccess';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import SuccessPage from './components/pricing/SuccessPage';
import LandingPage from './components/LandingPage';
import LanguageSelectorModal from './components/LanguageSelectorModal';

// Shukla Paksha (Waxing Moon) Chart - 15 days x 24 hours
const shuklaPakshaChart = [
  // Day 1
  ['I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P'],
  // Day 2
  ['I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P'],
  // Day 3
  ['I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P'],
  // Day 4
  ['P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I'],
  // Day 5
  ['P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I'],
  // Day 6
  ['P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I'],
  // Day 7
  ['I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P'],
  // Day 8
  ['I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P'],
  // Day 9
  ['I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P'],
  // Day 10
  ['P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I'],
  // Day 11
  ['P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I'],
  // Day 12
  ['P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I'],
  // Day 13
  ['I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P'],
  // Day 14
  ['I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P'],
  // Day 15
  ['I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P']
];

// Krishna Paksha (Waning Moon) Chart - 15 days x 24 hours
const krishnaPakshaChart = [
  // Day 1
  ['P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I'],
  // Day 2
  ['P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I'],
  // Day 3
  ['P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I'],
  // Day 4
  ['I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P'],
  // Day 5
  ['I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P'],
  // Day 6
  ['I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P'],
  // Day 7
  ['P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I'],
  // Day 8
  ['P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I'],
  // Day 9
  ['P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I'],
  // Day 10
  ['I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P'],
  // Day 11
  ['I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P'],
  // Day 12
  ['I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P'],
  // Day 13
  ['P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I'],
  // Day 14
  ['P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I'],
  // Day 15
  ['P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I', 'P', 'I']
];

// Tithi names in Tamil and English with significance
const tithiNames = {
  shukla: [
    { tamil: 'பிரதமை', english: 'Pratipada', meaning: 'First day', significance: 'New beginnings' },
    { tamil: 'துவிதியை', english: 'Dwitiya', meaning: 'Second day', significance: 'Good for travel' },
    { tamil: 'திருதியை', english: 'Tritiya', meaning: 'Third day', significance: 'Akshaya Tritiya' },
    { tamil: 'சதுர்த்தி', english: 'Chaturthi', meaning: 'Fourth day', significance: 'Ganesh Chaturthi' },
    { tamil: 'பஞ்சமி', english: 'Panchami', meaning: 'Fifth day', significance: 'Naga Panchami' },
    { tamil: 'சஷ்டி', english: 'Shashti', meaning: 'Sixth day', significance: 'Skanda Shashti' },
    { tamil: 'சப்தமி', english: 'Saptami', meaning: 'Seventh day', significance: 'Ratha Saptami' },
    { tamil: 'அஷ்டமி', english: 'Ashtami', meaning: 'Eighth day', significance: 'Durga Ashtami' },
    { tamil: 'நவமி', english: 'Navami', meaning: 'Ninth day', significance: 'Rama Navami' },
    { tamil: 'தசமி', english: 'Dashami', meaning: 'Tenth day', significance: 'Vijaya Dashami' },
    { tamil: 'ஏகாதசி', english: 'Ekadashi', meaning: 'Eleventh day', significance: 'Fasting day' },
    { tamil: 'துவாதசி', english: 'Dwadashi', meaning: 'Twelfth day', significance: 'Pradosham' },
    { tamil: 'திரயோதசி', english: 'Trayodashi', meaning: 'Thirteenth day', significance: 'Pradosham' },
    { tamil: 'சதுர்த்தசி', english: 'Chaturdashi', meaning: 'Fourteenth day', significance: 'Shiva worship' },
    { tamil: 'பௌர்ணமி', english: 'Purnima', meaning: 'Full Moon', significance: 'Satyanarayan Puja' }
  ],
  krishna: [
    { tamil: 'பிரதமை', english: 'Pratipada', meaning: 'First day', significance: 'New beginnings' },
    { tamil: 'துவிதியை', english: 'Dwitiya', meaning: 'Second day', significance: 'Good for travel' },
    { tamil: 'திருதியை', english: 'Tritiya', meaning: 'Third day', significance: 'Karwa Chauth' },
    { tamil: 'சதுர்த்தி', english: 'Chaturthi', meaning: 'Fourth day', significance: 'Sankashti Chaturthi' },
    { tamil: 'பஞ்சமி', english: 'Panchami', meaning: 'Fifth day', significance: 'Good for education' },
    { tamil: 'சஷ்டி', english: 'Shashti', meaning: 'Sixth day', significance: 'Kanda Shashti' },
    { tamil: 'சப்தமி', english: 'Saptami', meaning: 'Seventh day', significance: 'Kalashtami' },
    { tamil: 'அஷ்டமி', english: 'Ashtami', meaning: 'Eighth day', significance: 'Krishna Janmashtami' },
    { tamil: 'நவமி', english: 'Navami', meaning: 'Ninth day', significance: 'Good for worship' },
    { tamil: 'தசமி', english: 'Dashami', meaning: 'Tenth day', significance: 'Good for charity' },
    { tamil: 'ஏகாதசி', english: 'Ekadashi', meaning: 'Eleventh day', significance: 'Fasting day' },
    { tamil: 'துவாதசி', english: 'Dwadashi', meaning: 'Twelfth day', significance: 'Pradosham' },
    { tamil: 'திரயோதசி', english: 'Trayodashi', meaning: 'Thirteenth day', significance: 'Pradosham' },
    { tamil: 'சதுர்த்தசி', english: 'Chaturdashi', meaning: 'Fourteenth day', significance: 'Maha Shivaratri' },
    { tamil: 'அமாவாசை', english: 'Amavasya', meaning: 'New Moon', significance: 'Ancestor worship' }
  ]
};

// Get Tithi for a given date with enhanced accuracy and ending time
const getTithi = (date) => {
  const moonPhaseData = getMoonPhase(date);
  const paksha = moonPhaseData.paksha;
  const day = moonPhaseData.day;

  // Calculate tithi ending time
  // Each tithi is 12 degrees of moon-sun separation
  // Average tithi length varies from 19 hours to 26 hours
  const avgTithiLength = 0.984; // Average tithi length in days
  const currentTithiNumber = paksha === 'shukla' ? day : day + 15;

  // Calculate more accurate tithi length based on moon's position
  // Moon moves faster at perigee, slower at apogee
  const moonAnomaly = (moonPhaseData.moonAge * 13.176) % 360; // Approximate moon anomaly
  const eccentricityFactor = 1 + 0.0549 * Math.cos((moonAnomaly * Math.PI) / 180);
  const actualTithiLength = avgTithiLength / eccentricityFactor;

  // Calculate progress through current tithi
  const tithiProgress = (moonPhaseData.moonAge % actualTithiLength) / actualTithiLength;
  const remainingTithiFraction = 1 - tithiProgress;
  const remainingHours = remainingTithiFraction * actualTithiLength * 24;

  // Calculate ending time
  const endingTime = new Date(date.getTime() + (remainingHours * 60 * 60 * 1000));

  // Adjust for sunrise (tithi changes happen at sunrise, not midnight)
  const sunriseHour = 6; // Approximate sunrise
  if (endingTime.getHours() < sunriseHour) {
    endingTime.setHours(sunriseHour, 0, 0, 0);
  }

  // Calculate time remaining
  const hoursLeft = Math.floor(remainingHours);
  const minutesLeft = Math.round((remainingHours - hoursLeft) * 60);

  // Adjust for timezone (IST is UTC+5:30)
  const localDate = new Date(date);
  const istOffset = 5.5 * 60 * 60 * 1000; // IST offset in milliseconds
  const istDate = new Date(localDate.getTime() + istOffset);

  // Check if we need to adjust the tithi based on sunrise
  const hours = istDate.getHours();

  // If it's before sunrise (approx 6 AM), we might still be in previous tithi
  if (hours < sunriseHour && tithiProgress < 0.1) {
    // Very close to tithi boundary and before sunrise
    if (day > 1) {
      return {
        paksha: paksha,
        day: day - 1,
        moonAge: moonPhaseData.moonAge,
        endingTime: new Date(date.getTime() + (sunriseHour - hours) * 60 * 60 * 1000),
        hoursLeft: sunriseHour - hours,
        minutesLeft: 0,
        ...tithiNames[paksha][day - 2]
      };
    } else if (paksha === 'krishna') {
      // First day of Krishna paksha, go to last day of Shukla
      return {
        paksha: 'shukla',
        day: 15,
        moonAge: moonPhaseData.moonAge,
        endingTime: new Date(date.getTime() + (sunriseHour - hours) * 60 * 60 * 1000),
        hoursLeft: sunriseHour - hours,
        minutesLeft: 0,
        ...tithiNames.shukla[14]
      };
    }
  }

  // Determine next tithi info
  let nextTithi = {};
  if (day < 15) {
    nextTithi = {
      paksha: paksha,
      day: day + 1,
      ...tithiNames[paksha][day]
    };
  } else if (paksha === 'shukla') {
    // After Purnima, start Krishna paksha
    nextTithi = {
      paksha: 'krishna',
      day: 1,
      ...tithiNames.krishna[0]
    };
  } else {
    // After Amavasya, start Shukla paksha
    nextTithi = {
      paksha: 'shukla',
      day: 1,
      ...tithiNames.shukla[0]
    };
  }

  if (day >= 1 && day <= 15) {
    const tithiInfo = tithiNames[paksha][day - 1];
    return {
      paksha: paksha,
      day: day,
      moonAge: moonPhaseData.moonAge,
      endingTime: endingTime,
      hoursLeft: hoursLeft,
      minutesLeft: minutesLeft,
      nextTithi: nextTithi,
      ...tithiInfo
    };
  }

  // Default
  return {
    paksha: 'shukla',
    day: 1,
    moonAge: moonPhaseData.moonAge,
    endingTime: endingTime,
    hoursLeft: hoursLeft,
    minutesLeft: minutesLeft,
    nextTithi: nextTithi,
    ...tithiNames.shukla[0]
  };
};

// Moon phase calculation with improved accuracy
const getMoonPhase = (date) => {
  // Calculate days since new moon (January 6, 2000, 18:14 UTC)
  const baseNewMoon = new Date(2000, 0, 6, 18, 14, 0);
  const lunarCycle = 29.530588853; // Average lunar month in days

  // Get the difference in days
  const daysSinceBase = (date - baseNewMoon) / (1000 * 60 * 60 * 24);

  // Calculate number of lunar cycles
  const lunarCycles = daysSinceBase / lunarCycle;

  // Get the fractional part (0 to 1) representing the current moon phase
  const moonPhase = lunarCycles - Math.floor(lunarCycles);

  // Convert to days in lunar month (0 to 29.53)
  const daysIntoLunarMonth = moonPhase * lunarCycle;

  // Determine tithi (each tithi is 12 degrees or approximately 0.984 days)
  const tithiLength = lunarCycle / 30; // Approximately 0.984 days per tithi
  let tithiNumber = Math.floor(daysIntoLunarMonth / tithiLength) + 1;

  // Ensure tithi is within valid range
  if (tithiNumber > 30) tithiNumber = 30;
  if (tithiNumber < 1) tithiNumber = 1;

  // Determine paksha and day
  if (tithiNumber <= 15) {
    // Shukla Paksha (Waxing Moon)
    return {
      paksha: 'shukla',
      day: tithiNumber,
      moonAge: daysIntoLunarMonth
    };
  } else {
    // Krishna Paksha (Waning Moon)
    return {
      paksha: 'krishna',
      day: tithiNumber - 15,
      moonAge: daysIntoLunarMonth
    };
  }
};

// Major cities with coordinates and timezones
const locations = [
  { name: 'Chennai, India', lat: 13.0827, lng: 80.2707, timezone: 'Asia/Kolkata' },
  { name: 'Mumbai, India', lat: 19.0760, lng: 72.8777, timezone: 'Asia/Kolkata' },
  { name: 'Delhi, India', lat: 28.7041, lng: 77.1025, timezone: 'Asia/Kolkata' },
  { name: 'Bangalore, India', lat: 12.9716, lng: 77.5946, timezone: 'Asia/Kolkata' },
  { name: 'Kolkata, India', lat: 22.5726, lng: 88.3639, timezone: 'Asia/Kolkata' },
  { name: 'Madurai, India', lat: 9.9252, lng: 78.1198, timezone: 'Asia/Kolkata' },
  { name: 'Coimbatore, India', lat: 11.0168, lng: 76.9558, timezone: 'Asia/Kolkata' },
  { name: 'Trichy, India', lat: 10.7905, lng: 78.7047, timezone: 'Asia/Kolkata' },
  { name: 'London, UK', lat: 51.5074, lng: -0.1278, timezone: 'Europe/London' },
  { name: 'New York, USA', lat: 40.7128, lng: -74.0060, timezone: 'America/New_York' },
  { name: 'Los Angeles, USA', lat: 34.0522, lng: -118.2437, timezone: 'America/Los_Angeles' },
  { name: 'Toronto, Canada', lat: 43.6532, lng: -79.3832, timezone: 'America/Toronto' },
  { name: 'Sydney, Australia', lat: -33.8688, lng: 151.2093, timezone: 'Australia/Sydney' },
  { name: 'Singapore', lat: 1.3521, lng: 103.8198, timezone: 'Asia/Singapore' },
  { name: 'Dubai, UAE', lat: 25.2048, lng: 55.2708, timezone: 'Asia/Dubai' },
  { name: 'Kuala Lumpur, Malaysia', lat: 3.1390, lng: 101.6869, timezone: 'Asia/Kuala_Lumpur' },
  { name: 'Colombo, Sri Lanka', lat: 6.9271, lng: 79.8612, timezone: 'Asia/Colombo' },
  { name: 'Custom Location', lat: 0, lng: 0, timezone: 'UTC' }
];

// Timezones list
const timezones = [
  'UTC',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Dubai',
  'Asia/Colombo',
  'Asia/Kuala_Lumpur',
  'Europe/London',
  'Europe/Paris',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'America/Toronto',
  'Australia/Sydney',
  'Pacific/Auckland'
];

// Sunrise/Sunset calculation - Improved version
const calculateSunTimes = (date, lat, lng) => {
  const toRad = (deg) => deg * Math.PI / 180;
  const toDeg = (rad) => rad * 180 / Math.PI;

  // Get day of year
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date - start;
  const dayOfYear = Math.floor(diff / (1000 * 60 * 60 * 24));

  // Calculate the equation of time and solar declination
  const P = Math.asin(0.39795 * Math.cos(toRad(0.98563 * (dayOfYear - 173) + 1.914 * Math.sin(toRad(0.98563 * (dayOfYear - 2))))));

  // Calculate sunrise/sunset hour angle
  const latRad = toRad(lat);
  const hourAngle = Math.acos(-Math.tan(latRad) * Math.tan(P));

  // Calculate time corrections
  const timeCorrection = -0.06571 * hourAngle - 0.06251;
  const noonTime = 12 - lng / 15 - timeCorrection;
  const halfDayLength = toDeg(hourAngle) / 15;

  // Calculate sunrise and sunset times in decimal hours
  const sunriseDecimal = noonTime - halfDayLength;
  const sunsetDecimal = noonTime + halfDayLength;

  // Create date objects with correct times
  const sunrise = new Date(date);
  const sunriseHours = Math.floor(sunriseDecimal);
  const sunriseMinutes = Math.round((sunriseDecimal - sunriseHours) * 60);
  sunrise.setHours(sunriseHours, sunriseMinutes, 0, 0);

  const sunset = new Date(date);
  const sunsetHours = Math.floor(sunsetDecimal);
  const sunsetMinutes = Math.round((sunsetDecimal - sunsetHours) * 60);
  sunset.setHours(sunsetHours, sunsetMinutes, 0, 0);

  // Adjust for edge cases
  if (sunrise.getHours() < 4 || sunrise.getHours() > 8) {
    // If sunrise is outside reasonable range, default to 6 AM
    sunrise.setHours(6, 0, 0, 0);
  }

  if (sunset.getHours() < 16 || sunset.getHours() > 20) {
    // If sunset is outside reasonable range, default to 6 PM
    sunset.setHours(18, 0, 0, 0);
  }

  return { sunrise, sunset };
};

function MainAppContent({ isDarkMode, setIsDarkMode }) {
  const { t, i18n } = useTranslation();
  const { signOut, daysUntilExpiry, profile, updateProfile } = useAuth();
  const navigate = useNavigate();
  const daysLeft = daysUntilExpiry();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [notifications, setNotifications] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [manualPaksha, setManualPaksha] = useState('shukla');
  const [manualDay, setManualDay] = useState(1);
  const [showInfo, setShowInfo] = useState(false);
  const [activeTab, setActiveTab] = useState('home'); // 'home', 'shukla', 'krishna', 'suntimes', 'tithi'
  const [selectedDate, setSelectedDate] = useState(currentTime);
  const [selectedLocation, setSelectedLocation] = useState(locations[0]);
  const [customLat, setCustomLat] = useState(locations[0].lat);
  const [customLng, setCustomLng] = useState(locations[0].lng);
  const [selectedTimezone, setSelectedTimezone] = useState(locations[0].timezone);
  const [gpsLoading, setGpsLoading] = useState(false);

  // Language onboarding state
  const [showLangSelector, setShowLangSelector] = useState(false);

  useEffect(() => {
    if (profile && !profile.preferred_language) {
      // Check localStorage as fallback if the DB column doesn't exist yet
      const wasChosenLocally = localStorage.getItem(`lang_chosen_${profile.id}`);
      if (!wasChosenLocally) {
        setShowLangSelector(true);
      }
    }
  }, [profile]);

  const handleLanguageSelect = async (langCode) => {
    setShowLangSelector(false);
    if (profile) {
      localStorage.setItem(`lang_chosen_${profile.id}`, 'true');
      // Attempt to save to DB, but don't block if column is missing
      try {
        await updateProfile({ preferred_language: langCode });
      } catch (e) {
        console.warn('Could not save language to DB (column might be missing), saved locally instead.');
      }
    }
  };

  // Handle location change
  const handleLocationChange = (locationName) => {
    const location = locations.find(l => l.name === locationName);
    if (location) {
      setSelectedLocation(location);
      setCustomLat(location.lat);
      setCustomLng(location.lng);
      setSelectedTimezone(location.timezone);
    }
  };

  // Get current location using GPS
  const getCurrentLocation = () => {
    setGpsLoading(true);
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setSelectedLocation(locations.find(l => l.name === 'Custom Location'));
          setCustomLat(position.coords.latitude);
          setCustomLng(position.coords.longitude);
          setGpsLoading(false);
        },
        (error) => {
          alert('Unable to get your location. Please check permissions.');
          setGpsLoading(false);
        }
      );
    } else {
      alert('Geolocation is not supported by your browser.');
      setGpsLoading(false);
    }
  };

  // State for sun times
  const [sunTimes, setSunTimes] = useState(null);
  const [sunTimesLoading, setSunTimesLoading] = useState(false);

  // City-specific average sunrise/sunset times for better accuracy
  const cityTimings = {
    'Chennai, India': { sunrise: [5, 55], sunset: [18, 15] },
    'Mumbai, India': { sunrise: [6, 15], sunset: [18, 50] },
    'Delhi, India': { sunrise: [5, 50], sunset: [18, 35] },
    'Bangalore, India': { sunrise: [6, 10], sunset: [18, 25] },
    'Kolkata, India': { sunrise: [5, 25], sunset: [17, 45] },
    'Madurai, India': { sunrise: [6, 5], sunset: [18, 20] },
    'Coimbatore, India': { sunrise: [6, 15], sunset: [18, 30] },
    'Trichy, India': { sunrise: [6, 5], sunset: [18, 20] },
  };

  // Fetch accurate sun times (using calculation method)
  const fetchAccurateSunTimes = async (lat, lng) => {
    setSunTimesLoading(true);
    try {
      let sunTimes;

      // Check if we have specific timings for this city
      if (cityTimings[selectedLocation.name]) {
        const timing = cityTimings[selectedLocation.name];
        const sunrise = new Date(currentTime);
        sunrise.setHours(timing.sunrise[0], timing.sunrise[1], 0, 0);

        const sunset = new Date(currentTime);
        sunset.setHours(timing.sunset[0], timing.sunset[1], 0, 0);

        sunTimes = { sunrise, sunset };
      } else {
        // Use calculation for other locations
        sunTimes = calculateSunTimes(currentTime, lat, lng);
      }

      setSunTimes(sunTimes);
      setSunTimesLoading(false);
      return sunTimes;

    } catch (error) {
      console.error('Error calculating sun times:', error);
      setSunTimesLoading(false);

      // Fallback to default times
      const sunrise = new Date(currentTime);
      const sunset = new Date(currentTime);
      sunrise.setHours(6, 0, 0, 0);
      sunset.setHours(18, 0, 0, 0);

      const fallbackTimes = { sunrise, sunset };
      setSunTimes(fallbackTimes);
      return fallbackTimes;
    }
  };

  // Memoize the date string to avoid infinite loops
  const currentDateString = currentTime.toDateString();

  // Effect to fetch sun times when location changes
  useEffect(() => {
    if (activeTab === 'suntimes') {
      const lat = selectedLocation.name === 'Custom Location' ? customLat : selectedLocation.lat;
      const lng = selectedLocation.name === 'Custom Location' ? customLng : selectedLocation.lng;

      fetchAccurateSunTimes(lat, lng);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, customLat, customLng, currentDateString, activeTab]); // Re-fetch when date changes or tab is opened

  // Get sun times for display
  const getSunTimes = () => {
    if (sunTimes) {
      return sunTimes;
    }

    // Fallback to calculation if API data not available
    const lat = selectedLocation.name === 'Custom Location' ? customLat : selectedLocation.lat;
    const lng = selectedLocation.name === 'Custom Location' ? customLng : selectedLocation.lng;
    return calculateSunTimes(currentTime, lat, lng);
  };

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Calculate time remaining for current tithi
  const getTimeRemaining = (endingTime) => {
    if (!endingTime) return null;

    const now = new Date();
    const diff = endingTime - now;

    if (diff <= 0) return { hours: 0, minutes: 0, seconds: 0, expired: true };

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return { hours, minutes, seconds, expired: false };
  };

  // Get current hour and moon phase
  const currentHour = currentTime.getHours();
  const moonPhase = getMoonPhase(currentTime);

  // Determine active nostril
  const getActiveNostril = () => {
    const paksha = manualMode ? manualPaksha : moonPhase.paksha;
    const day = manualMode ? manualDay : moonPhase.day;
    const chart = paksha === 'shukla' ? shuklaPakshaChart : krishnaPakshaChart;

    if (day >= 1 && day <= 15) {
      return chart[day - 1][currentHour];
    }
    return 'I';
  };

  const activeNostril = getActiveNostril();
  const nostrilName = activeNostril === 'I' ? t('ida') : t('pingala');
  const nostrilSymbol = activeNostril === 'I' ? t('idaSymbol') : t('pingalaSymbol');

  // Notification handling
  useEffect(() => {
    if (notifications && 'Notification' in window) {
      Notification.requestPermission();
    }
  }, [notifications]);

  useEffect(() => {
    if (notifications && currentTime.getMinutes() === 0 && currentTime.getSeconds() === 0) {
      if (Notification.permission === 'granted') {
        new Notification('Pranayama Rhythm', {
          body: `Active nostril changed to ${nostrilName}`,
          icon: '/icon-192x192.png'
        });
      }
    }
  }, [currentTime, notifications, nostrilName]);

  // Table component for displaying the charts
  const PakshaTable = ({ title, chart, paksha }) => {
    const isShukla = paksha === 'shukla';
    const themeColor = isShukla ? 'blue' : 'gold';
    const bgColor = isDarkMode ? 'glass-card' : (isShukla ? 'bg-blue-50/50' : 'bg-gold-50/50');

    return (
      <div className={`rounded-2xl p-2 sm:p-5 ${isDarkMode ? 'glass-card' : 'bg-white/80'} backdrop-blur-md shadow-xl border ${isDarkMode ? 'border-white/5' : (isShukla ? 'border-blue-100' : 'border-gold-100')}`}>
        <h2 className={`text-xl sm:text-2xl font-black mb-6 text-center ${isDarkMode ? 'text-white' : 'text-slate-900'} uppercase tracking-tighter`}>
          {title}
        </h2>
        <div className="overflow-x-auto relative">
          <table className={`w-full text-sm relative ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <thead>
              <tr className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <th className={`p-1 sm:p-2 text-left sticky left-0 z-10 ${isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-white text-gray-600'}`}>{t('day')}</th>
                {[...Array(24)].map((_, i) => (
                  <th key={i} className={`p-0.5 sm:p-1 text-center min-w-[28px] sm:min-w-[35px] text-xs ${i === currentHour
                    ? 'bg-gold-500/20 text-gold-500 font-bold'
                    : ''
                    } ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                    {i + 1}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chart.map((row, dayIndex) => {
                const isCurrentDay = (
                  (manualMode && manualPaksha === paksha && manualDay === dayIndex + 1) ||
                  (!manualMode && moonPhase.paksha === paksha && moonPhase.day === dayIndex + 1)
                );
                return (
                  <tr key={dayIndex} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'
                    } ${isCurrentDay ? 'bg-blue-500 bg-opacity-10' : ''}`}>
                    <td className={`p-1 sm:p-2 font-medium sticky left-0 z-10 ${isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-white text-gray-600'
                      } ${isCurrentDay ? 'font-bold text-blue-500' : ''}`}>
                      {dayIndex + 1}
                    </td>
                    {row.map((cell, hourIndex) => (
                      <td
                        key={hourIndex}
                        className={`p-0.5 sm:p-1 text-center font-bold text-xs sm:text-base ${cell === 'I'
                          ? 'text-blue-500'
                          : 'text-gold-500'
                          } ${hourIndex === currentHour && isCurrentDay
                            ? 'bg-gold-500/20 ring-1 ring-gold-500 rounded'
                            : ''
                          }`}
                      >
                        {cell === 'I' ? t('idaSymbol') : t('pingalaSymbol')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className={`mt-4 text-xs sm:text-sm text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          <p className="font-medium">{t('idaSymbol')} = {t('ida')} | {t('pingalaSymbol')} = {t('pingala')}</p>
          <p>{t('hoursLabel')}: 1-24 ({t('fromLabel')} {t('sunrise')} {t('fromSunrise')})</p>
          <p className="mt-2 text-yellow-600 font-medium">
            ⏰ {t('hour')}: {currentHour + 1} (24h) |
            {manualMode ? ` 📅 ${t('manualDay')}: ${manualDay}` : ` 📅 ${t('day')}: ${moonPhase.day}`} |
            {t('activeNostril')}: {getActiveNostril() === 'I' ? ` ${t('ida')}` : ` ${t('pingala')}`}
          </p>
          <p className="text-xs mt-1">💡 {t('currentPosition')}</p>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 font-sans ${isDarkMode
      ? 'bg-cosmic-900 text-slate-200'
      : 'bg-[#F8FAFC] text-slate-900'}`}>
      <LanguageSelectorModal
        isOpen={showLangSelector}
        onSelect={handleLanguageSelect}
      />
      <div className="max-w-6xl mx-auto p-2 sm:p-4">
        {/* Header */}
        <header className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <img
              src="/icon-192x192.png"
              alt="Logo"
              className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
            />
            <div className={`text-2xl sm:text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              <span className="text-blue-500">{t('appTitle').charAt(0)}</span>{t('appTitle').slice(1)}
            </div>
            <div className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('appSubtitle')}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <div className="relative group">
              <select
                onChange={(e) => i18n.changeLanguage(e.target.value)}
                value={i18n.language}
                className={`appearance-none p-2 pl-3 pr-8 rounded-lg outline-none cursor-pointer font-medium text-sm transition-colors border-none focus:ring-0 ${isDarkMode
                  ? 'bg-transparent hover:bg-gray-800 text-gray-400 hover:text-white'
                  : 'bg-transparent hover:bg-gray-200 text-gray-600 hover:text-gray-900'
                  }`}
                style={{ textAlignLast: 'center' }}
              >
                <option value="en">EN</option>
                <option value="ta">தமிழ்</option>
                <option value="es">ES</option>
                <option value="fr">FR</option>
                <option value="de">DE</option>
                <option value="hi">हिन्दी</option>
              </select>
              <Languages className={`w-4 h-4 absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            </div>

            <button
              onClick={() => setShowInfo(!showInfo)}
              className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-200'}`}
            >
              <Info className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
            </button>
            <button
              onClick={() => setNotifications(!notifications)}
              className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-200'}`}
            >
              {notifications ? (
                <Bell className="w-5 h-5 text-blue-500" />
              ) : (
                <BellOff className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
              )}
            </button>
            {profile?.subscription_tier === 'Free' && (
              <button
                onClick={() => navigate('/pricing')}
                className={`flex items-center space-x-1 px-3 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs font-bold transition-all transform hover:scale-105 shadow-lg shadow-blue-600/20`}
              >
                <Crown className="w-4 h-4" />
                <span>UPGRADE</span>
              </button>
            )}

            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-200'}`}
            >
              {isDarkMode ? (
                <Sun className="w-5 h-5 text-yellow-400" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600" />
              )}
            </button>

            <button
              onClick={signOut}
              className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-200'} text-red-500`}
              title="Sign Out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* Expiration Warning */}
        {daysLeft !== null && daysLeft <= 14 && (
          <div className="mb-6 bg-orange-500/10 border border-orange-500/20 p-4 rounded-2xl flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-orange-500/20 rounded-xl">
                <Clock className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">Free Trial Ending Soon</p>
                <p className="text-gray-400 text-xs">Your access will be restricted in {daysLeft} days. Upgrade now to keep exploring.</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/pricing')}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-400 text-white text-xs font-black rounded-xl transition-all"
            >
              UPGRADE
            </button>
          </div>
        )}

        {/* Tab Navigation */}
        <div className={`flex space-x-1 mb-8 p-1.5 rounded-2xl overflow-x-auto ${isDarkMode ? 'bg-gray-800' : 'bg-slate-100'} shadow-inner`}>
          <button
            onClick={() => setActiveTab('home')}
            className={`flex-shrink-0 py-2.5 px-4 rounded-xl font-black transition-all text-xs uppercase tracking-widest ${activeTab === 'home'
              ? `${isDarkMode ? 'bg-gray-700 text-white' : 'bg-white text-slate-900 shadow-md transform scale-[1.02]'}`
              : `${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`
              }`}
          >
            {t('tabHome')}
          </button>
          <button
            onClick={() => setActiveTab('shukla')}
            className={`flex-shrink-0 py-2.5 px-4 rounded-xl font-black transition-all text-xs uppercase tracking-widest ${activeTab === 'shukla'
              ? `${isDarkMode ? 'bg-gray-700 text-white' : 'bg-blue-500 text-white shadow-xl shadow-blue-500/20 transform scale-[1.02]'}`
              : `${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`
              }`}
          >
            {t('tabShukla')}
          </button>
          <button
            onClick={() => setActiveTab('krishna')}
            className={`flex-shrink-0 py-2.5 px-4 rounded-xl font-black transition-all text-xs uppercase tracking-widest ${activeTab === 'krishna'
              ? `${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gold-500 text-white shadow-xl shadow-gold-500/20 transform scale-[1.02]'}`
              : `${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`
              }`}
          >
            {t('tabKrishna')}
          </button>
          <button
            onClick={() => setActiveTab('suntimes')}
            className={`flex-shrink-0 py-2.5 px-4 rounded-xl font-black transition-all text-xs uppercase tracking-widest ${activeTab === 'suntimes'
              ? `${isDarkMode ? 'bg-gray-700 text-white' : 'bg-amber-500 text-white shadow-xl shadow-amber-500/20 transform scale-[1.02]'}`
              : `${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`
              }`}
          >
            ☀️ {t('tabSunTimes')}
          </button>
          <button
            onClick={() => setActiveTab('tithi')}
            className={`flex-shrink-0 py-2.5 px-4 rounded-xl font-black transition-all text-xs uppercase tracking-widest ${activeTab === 'tithi'
              ? `${isDarkMode ? 'bg-gray-700 text-white' : 'bg-indigo-500 text-white shadow-xl shadow-indigo-500/20 transform scale-[1.02]'}`
              : `${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`
              }`}
          >
            🌙 {t('tabTithi')}
          </button>
        </div>

        {/* Conditional Content Rendering based on active tab */}
        <div className="relative">
          {activeTab === 'home' && (
            <>
              {/* Info Panel */}
              {showInfo && (
                <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
                  <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {t('aboutTitle')}
                  </h3>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {t('aboutText')}
                  </p>
                </div>
              )}

              {/* Main Display */}
              <div className={`rounded-[32px] p-6 sm:p-8 lg:p-10 mb-8 shadow-2xl transition-all duration-500 ${isDarkMode
                ? 'bg-gray-800'
                : (activeNostril === 'I'
                  ? 'bg-gradient-to-br from-blue-50 to-white border border-blue-100 shadow-blue-500/10'
                  : 'bg-gradient-to-br from-gold-50 to-white border border-gold-100 shadow-gold-500/10')
                }`}>

                <div className="text-center mb-6">
                  <div className={`text-4xl sm:text-6xl mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {currentTime.toLocaleTimeString(i18n.language)}
                  </div>
                  <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {currentTime.toLocaleDateString(i18n.language, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                  {/* Current Tithi indicator */}
                  <div className={`mt-2 inline-flex items-center px-3 py-1 rounded-full ${isDarkMode ? 'bg-gray-700 border border-gray-600' : 'bg-yellow-50 border border-yellow-200'
                    }`}>
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'
                      }`}>
                      {(() => {
                        const tithi = getTithi(currentTime);
                        const tithiKey = (tithi.paksha === 'krishna' && tithi.day === 15) ? 'tithiName16' : `tithiName${tithi.day}`;
                        const tithiName = t(tithiKey);
                        const pakshaName = tithi.paksha === 'shukla' ? t('shukla') : t('krishna');
                        return `📅 ${tithiName} • ${pakshaName} ${tithi.day}`;
                      })()}
                    </span>
                  </div>
                </div>

                <div className="text-center">
                  <div className={`text-6xl sm:text-8xl font-bold mb-4 ${activeNostril === 'I' ? 'text-blue-500' : 'text-gold-500'
                    }`}>
                    {nostrilSymbol}
                  </div>
                  <div className={`text-xl sm:text-2xl font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {nostrilName}
                  </div>
                  <div className={`text-base sm:text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {t('activeNostril')}
                  </div>
                </div>

                {/* Breathing Wave Animation */}
                <div className="mt-8 flex justify-center items-center space-x-1">
                  {['I', 'P', 'I', 'P', 'I', 'P', 'I', 'P'].map((letter, i) => (
                    <div
                      key={i}
                      className={`text-2xl font-bold transition-all duration-1000 ${letter === activeNostril ?
                        (letter === 'I' ? 'text-blue-500 scale-125' : 'text-gold-500 scale-125') :
                        (isDarkMode ? 'text-gray-600' : 'text-gray-400')
                        }`}
                      style={{
                        animation: `breathe ${4 + i * 0.2}s ease-in-out infinite`,
                        animationDelay: `${i * 0.1}s`
                      }}
                    >
                      {letter === 'I' ? t('idaSymbol') : t('pingalaSymbol')}
                    </div>
                  ))}
                </div>

                {/* Calendar Legend */}
                <div className={`mt-3 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  <div className="flex items-center justify-center gap-4">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <span>{t('legendSpecial')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="ring-2 ring-blue-500 w-4 h-4 rounded"></div>
                      <span>{t('today')}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Moon Phase Display */}
              <div className={`rounded-lg p-4 mb-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {t('currentMoonPhase')}
                    </div>
                    <div className={`text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {manualMode ?
                        `${t(manualPaksha)} ${t('day')} ${manualDay}` :
                        `${t(moonPhase.paksha)} ${t('day')} ${moonPhase.day}`
                      }
                    </div>
                    {!manualMode && (() => {
                      const currentTithi = getTithi(currentTime);
                      const remaining = getTimeRemaining(currentTithi.endingTime);
                      return currentTithi.endingTime && remaining ? (
                        <div className={`text-xs mt-1 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                          {t('tithiEndsAt')} {currentTithi.endingTime.toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })} ({remaining.expired ? t('tithiChanged') : `${remaining.hours}${t('hourUnit')} ${remaining.minutes}${t('minuteUnit')}`})
                        </div>
                      ) : null;
                    })()}
                  </div>
                  <div className="text-3xl">
                    {(manualMode ? manualPaksha : moonPhase.paksha) === 'shukla' ? '🌒' : '🌘'}
                  </div>
                </div>
              </div>

              {/* Manual Override */}
              <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {t('manualOverride')}
                  </h3>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={manualMode}
                      onChange={(e) => setManualMode(e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`w-10 h-5 rounded-full transition-colors ${manualMode ? 'bg-blue-500' : (isDarkMode ? 'bg-gray-600' : 'bg-gray-300')
                      }`}>
                      <div className={`w-4 h-4 bg-white rounded-full transition-transform ${manualMode ? 'translate-x-5' : 'translate-x-0.5'
                        } mt-0.5`} />
                    </div>
                  </label>
                </div>

                {manualMode && (
                  <div className="space-y-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {t('selectPaksha')}
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => setManualPaksha('shukla')}
                          className={`p-3 rounded-xl font-black text-sm transition-all ${manualPaksha === 'shukla' ?
                            'bg-blue-600 text-white shadow-lg shadow-blue-600/30' :
                            (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-slate-100 text-slate-500 hover:bg-slate-200')
                            }`}
                        >
                          {t('shuklaHeader')}
                        </button>
                        <button
                          onClick={() => setManualPaksha('krishna')}
                          className={`p-3 rounded-xl font-black text-sm transition-all ${manualPaksha === 'krishna' ?
                            'bg-gold-600 text-white shadow-lg shadow-gold-600/30' :
                            (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-slate-100 text-slate-500 hover:bg-slate-200')
                            }`}
                        >
                          {t('krishnaHeader')}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {t('dayOfFortnight')}: {manualDay}
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="15"
                        value={manualDay}
                        onChange={(e) => setManualDay(parseInt(e.target.value))}
                        className="w-full"
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {activeTab === 'shukla' && (
            <PakshaTable
              title={`${t('shuklaHeader')} (${t('waxingMoonLabel')})`}
              chart={shuklaPakshaChart}
              paksha="shukla"
            />
          )}

          {activeTab === 'krishna' && (
            <PakshaTable
              title={`${t('krishnaHeader')} (${t('waningMoonLabel')})`}
              chart={krishnaPakshaChart}
              paksha="krishna"
            />
          )}

          {activeTab === 'suntimes' && (
            <div className={`rounded-lg p-4 sm:p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
              <div className="flex justify-between items-center mb-6">
                <h2 className={`text-xl sm:text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  ☀️ {t('sunTimesTitle')}
                </h2>
                <button
                  onClick={() => {
                    const lat = selectedLocation.name === 'Custom Location' ? customLat : selectedLocation.lat;
                    const lng = selectedLocation.name === 'Custom Location' ? customLng : selectedLocation.lng;
                    fetchAccurateSunTimes(lat, lng);
                  }}
                  className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                  title={t('refreshSunTimes') || "Refresh sun times"}
                >
                  🔄
                </button>
              </div>

              {/* Location Selection */}
              <div className="space-y-4 mb-6">
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('selectLocation')}
                </label>
                <select
                  value={selectedLocation.name}
                  onChange={(e) => handleLocationChange(e.target.value)}
                  className={`w-full p-2 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'
                    }`}
                >
                  {locations.map((loc) => (
                    <option key={loc.name} value={loc.name}>
                      {loc.name === 'Custom Location' ? t('customLocation') : (t(loc.name.toLowerCase()) || loc.name)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Use Current Location Button */}
              <button
                onClick={getCurrentLocation}
                disabled={gpsLoading}
                className={`w-full p-3 rounded-lg flex items-center justify-center space-x-2 ${isDarkMode
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
                  } transition-colors disabled:opacity-50`}
              >
                {gpsLoading ? (
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                ) : (
                  <MapPin size={18} />
                )}
                <span>{gpsLoading ? t('locatingLocation') : t('useCurrentLocation')}</span>
              </button>

              {/* Timezone Selection */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('timezone')}
                </label>
                <select
                  value={selectedTimezone}
                  onChange={(e) => setSelectedTimezone(e.target.value)}
                  className={`w-full p-2 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'
                    }`}
                >
                  {timezones.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </div>

              {/* Coordinates Input */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('latitude')}
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    value={customLat}
                    onChange={(e) => setCustomLat(parseFloat(e.target.value))}
                    disabled={selectedLocation.name !== 'Custom Location'}
                    className={`w-full p-2 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'
                      } ${selectedLocation.name !== 'Custom Location' ? 'opacity-50' : ''}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('longitude')}
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    value={customLng}
                    onChange={(e) => setCustomLng(parseFloat(e.target.value))}
                    disabled={selectedLocation.name !== 'Custom Location'}
                    className={`w-full p-2 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'
                      } ${selectedLocation.name !== 'Custom Location' ? 'opacity-50' : ''}`}
                  />
                </div>
              </div>


              {/* Sun Times Display */}
              <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                {sunTimesLoading ? (
                  <div className="text-center py-8">
                    <div className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {t('loadingSunTimes')}
                    </div>
                  </div>
                ) : (
                  <>
                    {(() => {
                      const sunTimes = getSunTimes();
                      const dayLength = (sunTimes.sunset - sunTimes.sunrise) / (1000 * 60 * 60);
                      const currentHourFromSunrise = Math.floor((currentTime - sunTimes.sunrise) / (1000 * 60 * 60)) + 1;

                      return (
                        <>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                🌅 {t('sunrise')}
                              </span>
                              <span className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {sunTimes.sunrise.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>

                            <div className="flex justify-between items-center">
                              <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                🌇 {t('sunset')}
                              </span>
                              <span className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {sunTimes.sunset.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>

                            <div className="flex justify-between items-center">
                              <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                ⏱️ {t('dayLength')}
                              </span>
                              <span className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {Math.floor(dayLength)}{t('hourUnit')} {Math.round((dayLength % 1) * 60)}{t('minuteUnit')}
                              </span>
                            </div>

                            <div className="pt-3 border-t border-gray-600">
                              <div className="flex justify-between items-center">
                                <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                  🕐 {t('traditionalHour')}
                                </span>
                                <span className={`text-lg font-semibold ${currentTime < sunTimes.sunrise
                                  ? 'text-gray-500'
                                  : isDarkMode ? 'text-yellow-400' : 'text-yellow-600'
                                  }`}>
                                  {currentTime < sunTimes.sunrise
                                    ? t('preSunrise')
                                    : currentTime > sunTimes.sunset
                                      ? t('postSunset')
                                      : t('hourOf12', { current: currentHourFromSunrise })
                                  }
                                </span>
                              </div>

                              <div className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-center`}>
                                {t('currentNostril')}: {nostrilName}
                              </div>
                            </div>
                          </div>

                          <div className={`mt-4 p-3 rounded-lg ${isDarkMode ? 'bg-gray-600' : 'bg-gray-100'}`}>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              ✅ {t('sunTimesDisclaimer1')} {currentTime.toLocaleDateString(i18n.language)}
                            </p>
                            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              📊 {t('sunTimesDisclaimer2')}
                            </p>
                            <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              💡 {t('sunTimesDisclaimer3')}
                            </p>
                          </div>
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          )}

          {activeTab === 'tithi' && (
            <div className={`rounded-lg p-4 sm:p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
              <h2 className={`text-xl sm:text-2xl font-bold mb-6 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                🌙 {t('tithiTitle')}
              </h2>

              {/* Current Tithi Display */}
              <div className={`rounded-lg p-4 mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {t('todaysTithi')}
                </h3>
                {(() => {
                  const currentTithi = getTithi(currentTime);
                  return (
                    <div className="text-center">
                      <div className={`text-3xl font-bold mb-2 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                        {t(currentTithi.paksha === 'krishna' && currentTithi.day === 15 ? 'tithiName16' : `tithiName${currentTithi.day}`)}
                      </div>
                      <div className={`text-xl mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      </div>
                      <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {currentTithi.paksha === 'shukla' ? t('shuklaPaksha') : t('krishnaPaksha')} - {t('day')} {currentTithi.day}
                      </div>
                      <div className={`text-sm mt-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        {t(currentTithi.paksha === 'shukla' ? `sigShukla${currentTithi.day}` : `sigKrishna${currentTithi.day}`)}
                      </div>

                      {/* Tithi ending time */}
                      <div className={`mt-3 p-2 rounded ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                        <div className={`text-sm font-semibold ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                          ⏰ {t('tithiEnds')}
                        </div>
                        <div className={`text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {currentTithi.endingTime ? currentTithi.endingTime.toLocaleTimeString(i18n.language, {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          }) : 'N/A'}
                        </div>

                        {/* Progress bar */}
                        <div className={`mt-2 w-full h-2 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}>
                          <div
                            className={`h-full rounded-full transition-all duration-1000 ${isDarkMode ? 'bg-orange-500' : 'bg-orange-400'
                              }`}
                            style={{
                              width: `${(() => {
                                const remaining = getTimeRemaining(currentTithi.endingTime);
                                if (!remaining || remaining.expired) return '100%';
                                // Assuming average tithi is ~24 hours
                                const totalMinutes = 24 * 60;
                                const remainingMinutes = remaining.hours * 60 + remaining.minutes;
                                const progress = ((totalMinutes - remainingMinutes) / totalMinutes) * 100;
                                return `${Math.min(100, Math.max(0, progress))}%`;
                              })()}`
                            }}
                          />
                        </div>

                        <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {(() => {
                            const remaining = getTimeRemaining(currentTithi.endingTime);
                            if (!remaining) return t('calculating');
                            if (remaining.expired) return t('tithiChanged');
                            return `${remaining.hours}${t('hourUnit')} ${remaining.minutes}${t('minuteUnit')} ${remaining.seconds}${t('secondUnit')} ${t('remaining')}`;
                          })()}
                        </div>
                        {currentTithi.nextTithi && (
                          <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {t('next')}: {t(currentTithi.nextTithi.paksha === 'krishna' && currentTithi.nextTithi.day === 15 ? 'tithiName16' : `tithiName${currentTithi.nextTithi.day}`)}
                          </div>
                        )}
                      </div>

                      <div className={`text-sm mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {currentTime.toLocaleDateString(i18n.language, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </div>
                      {/* Debug info - remove in production */}
                      <div className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        {t('moonAge')}: {currentTithi.moonAge ? currentTithi.moonAge.toFixed(2) : 'N/A'} {t('days')}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Date Picker - Only show in tithi tab */}
              {activeTab === 'tithi' && (
                <div className={`rounded-lg p-4 mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {t('selectDate')}
                  </h3>

                  <div className="mb-3 flex gap-2 justify-center">
                    <button
                      onClick={() => {
                        const yesterday = new Date(selectedDate);
                        yesterday.setDate(yesterday.getDate() - 1);
                        setSelectedDate(yesterday);
                      }}
                      className={`px-3 py-1 rounded transition-colors ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                        }`}
                    >
                      ← {t('yesterday')}
                    </button>
                    <button
                      onClick={() => setSelectedDate(currentTime)}
                      className={`px-3 py-1 rounded font-medium transition-colors ${isDarkMode ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'
                        }`}
                    >
                      {t('today')}
                    </button>
                    <button
                      onClick={() => {
                        const tomorrow = new Date(selectedDate);
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        setSelectedDate(tomorrow);
                      }}
                      className={`px-3 py-1 rounded transition-colors ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                        }`}
                    >
                      {t('tomorrow')} →
                    </button>
                  </div>

                  <input
                    type="date"
                    value={selectedDate.toISOString().split('T')[0]}
                    onChange={(e) => setSelectedDate(new Date(e.target.value))}
                    className={`w-full p-3 rounded-lg ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-white text-gray-900'
                      }`}
                  />

                  {/* Selected Date Tithi */}
                  {selectedDate && (
                    <div className="mt-4">
                      {(() => {
                        const selectedTithi = getTithi(selectedDate);
                        return (
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-600' : 'bg-gray-50'}`}>
                            <div className={`text-lg font-semibold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                              {t(selectedTithi.paksha === 'krishna' && selectedTithi.day === 15 ? 'tithiName16' : `tithiName${selectedTithi.day}`)}
                            </div>
                            <div className={`text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              {selectedTithi.paksha === 'shukla' ? t('shuklaPaksha') : t('krishnaPaksha')} - {t('day')} {selectedTithi.day}
                            </div>
                            <div className={`text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                              {t(selectedTithi.paksha === 'shukla' ? `sigShukla${selectedTithi.day}` : `sigKrishna${selectedTithi.day}`)}
                            </div>

                            {/* Tithi timing for selected date */}
                            <div className={`mt-2 pt-2 border-t ${isDarkMode ? 'border-gray-500' : 'border-gray-300'}`}>
                              <div className={`text-sm ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                                ⏰ {t('tithiEnds')}: {selectedTithi.endingTime ? selectedTithi.endingTime.toLocaleTimeString(i18n.language, {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: true
                                }) : 'N/A'}
                              </div>
                              {selectedTithi.nextTithi && (
                                <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {t('next')}: {t(selectedTithi.nextTithi.paksha === 'krishna' && selectedTithi.nextTithi.day === 15 ? 'tithiName16' : `tithiName${selectedTithi.nextTithi.day}`)}
                                </div>
                              )}
                            </div>

                            <div className={`text-sm mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {selectedDate.toLocaleDateString(i18n.language, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}

              {/* Month View */}
              <div className={`rounded-lg p-4 mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {t('monthView')}
                </h3>
                <div className={`text-center mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {selectedDate.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' })}
                </div>
                <div className="grid grid-cols-7 gap-1 text-xs">
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                    <div key={day} className={`text-center font-semibold p-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {day}
                    </div>
                  ))}
                  {(() => {
                    const firstDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
                    const lastDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
                    const startPadding = firstDay.getDay();
                    const days = [];

                    // Add empty cells for padding
                    for (let i = 0; i < startPadding; i++) {
                      days.push(<div key={`pad-${i}`} />);
                    }

                    // Add days
                    for (let i = 1; i <= lastDay.getDate(); i++) {
                      const dayDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), i);
                      const dayTithi = getTithi(dayDate);
                      const isToday = dayDate.toDateString() === currentTime.toDateString();
                      const isSelected = dayDate.toDateString() === selectedDate.toDateString();

                      days.push(
                        <button
                          key={i}
                          onClick={() => setSelectedDate(dayDate)}
                          className={`p-1 rounded text-center relative ${isToday
                            ? 'ring-2 ring-blue-500'
                            : ''
                            } ${isSelected
                              ? (isDarkMode ? 'bg-yellow-600 text-white' : 'bg-yellow-400 text-gray-900')
                              : (isDarkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-white hover:bg-gray-100')
                            }`}
                          title={`${t(dayTithi.paksha === 'krishna' && dayTithi.day === 15 ? 'tithiName16' : `tithiName${dayTithi.day}`)}`}
                        >
                          <div className="font-medium">{i}</div>
                          <div className={`text-xs ${isSelected
                            ? (isDarkMode ? 'text-gray-200' : 'text-gray-700')
                            : (isDarkMode ? 'text-gray-400' : 'text-gray-500')
                            }`}>
                            {dayTithi.day}
                          </div>
                          {/* Special tithi indicator */}
                          {(dayTithi.day === 11 || dayTithi.day === 15 || dayTithi.day === 14) && (
                            <div className="absolute top-0 right-0 w-2 h-2 rounded-full bg-red-500"></div>
                          )}
                        </button>
                      );
                    }

                    return days;
                  })()}
                </div>
              </div>

              {/* Tithi List */}
              <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {t('tithiList')}
                </h3>

                <div className={`mb-4 p-2 rounded ${isDarkMode ? 'bg-gray-600' : 'bg-gray-50'} text-center`}>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    📍 {t('tithiHighlightInfo')}
                  </p>
                </div>

                {/* Shukla Paksha */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className={`font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                      🌒 {t('shuklaHeader')} ({t('waxingMoonLabel')})
                    </h4>
                    <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {t('waxingMoonLabel')}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {tithiNames.shukla.map((tithi, index) => {
                      const isCurrentTithi = !manualMode && moonPhase.paksha === 'shukla' && moonPhase.day === index + 1;
                      return (
                        <div key={index} className={`p-4 rounded-2xl transition-all ${isCurrentTithi
                          ? (isDarkMode ? 'bg-blue-900/40 ring-2 ring-blue-500 shadow-lg shadow-blue-500/20' : 'bg-blue-500 text-white shadow-lg shadow-blue-500/30')
                          : (isDarkMode ? 'bg-gray-800' : 'bg-white border border-slate-100 uppercase')
                          }`}>
                          <div>
                            <div className="flex justify-between items-start">
                              <span className={`font-black tracking-tight ${isCurrentTithi
                                ? 'text-white'
                                : (isDarkMode ? 'text-white' : 'text-slate-900')
                                }`}>
                                {index + 1}. {t(`tithiName${index + 1}`)}
                              </span>
                              {isCurrentTithi && <div className="w-2 h-2 bg-white rounded-full animate-pulse" />}
                            </div>
                            <div className={`text-[10px] sm:text-xs mt-1 font-bold uppercase tracking-widest ${isCurrentTithi ? 'text-white/80' : 'text-slate-400'}`}>
                              {t(`sigShukla${index + 1}`)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Krishna Paksha */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className={`font-semibold ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                      🌘 {t('krishnaHeader')} ({t('waningMoonLabel')})
                    </h4>
                    <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {t('waningMoonLabel')}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {tithiNames.krishna.map((tithi, index) => {
                      const isCurrentTithi = !manualMode && moonPhase.paksha === 'krishna' && moonPhase.day === index + 1;
                      return (
                        <div key={index} className={`p-4 rounded-2xl transition-all ${isCurrentTithi
                          ? (isDarkMode ? 'bg-orange-900/40 ring-2 ring-orange-500 shadow-lg shadow-orange-500/20' : 'bg-orange-500 text-white shadow-lg shadow-orange-500/30')
                          : (isDarkMode ? 'bg-gray-800' : 'bg-white border border-slate-100 uppercase')
                          }`}>
                          <div>
                            <div className="flex justify-between items-start">
                              <span className={`font-black tracking-tight ${isCurrentTithi
                                ? 'text-white'
                                : (isDarkMode ? 'text-white' : 'text-slate-900')
                                }`}>
                                {index + 1}. {t((index === 14) ? 'tithiName16' : `tithiName${index + 1}`)}
                              </span>
                              {isCurrentTithi && <div className="w-2 h-2 bg-white rounded-full animate-pulse" />}
                            </div>
                            <div className={`text-[10px] sm:text-xs mt-1 font-bold uppercase tracking-widest ${isCurrentTithi ? 'text-white/80' : 'text-slate-400'}`}>
                              {t(`sigKrishna${index + 1}`)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className={`mt-4 p-3 rounded-lg ${isDarkMode ? 'bg-gray-600' : 'bg-gray-100'}`}>
                <p className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  💡 {t('tithiTip1')}
                </p>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  📊 {t('tithiTip2')}
                </p>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  ⚠️ {t('tithiTip3')}
                </p>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  🌙 {t('tithiTip4')}
                </p>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {t('approxTimeLabel')}
                </p>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  📅 {t('specialTithisLabel')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes breathe {
          0%, 100% { transform: translateY(0); opacity: 0.7; }
          50% { transform: translateY(-10px); opacity: 1; }
        }
        
        /* Make sticky columns work properly on mobile */
        .sticky {
          position: sticky;
          left: 0;
          background-color: inherit;
        }
        
        /* Custom scrollbar for tables */
        .overflow-x-auto::-webkit-scrollbar {
          height: 8px;
        }
        
        .overflow-x-auto::-webkit-scrollbar-track {
          background: ${isDarkMode ? '#374151' : '#f3f4f6'};
          border-radius: 4px;
        }
        
        .overflow-x-auto::-webkit-scrollbar-thumb {
          background: ${isDarkMode ? '#4b5563' : '#d1d5db'};
          border-radius: 4px;
        }
        
        .overflow-x-auto::-webkit-scrollbar-thumb:hover {
          background: ${isDarkMode ? '#6b7280' : '#9ca3af'};
        }
      `}</style>
    </div>
  );
}


export default function App() {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const navigate = useNavigate(); // Initialize navigate

  useEffect(() => {
    // Handle Supabase auth redirects (especially for password recovery)
    const handleAuthRedirect = () => {
      const hash = window.location.hash;
      if (hash && hash.includes('type=recovery')) {
        console.log('Recovery hash detected, redirecting to /reset-password');
        navigate('/reset-password' + hash);
      }
    };

    handleAuthRedirect();
    // Also listen for hash changes
    window.addEventListener('hashchange', handleAuthRedirect);
    return () => window.removeEventListener('hashchange', handleAuthRedirect);
  }, [navigate]);

  // Global handler to suppress 'AbortError: signal is aborted without reason'
  useEffect(() => {
    const handleAbortError = (e) => {
      const isAbort =
        e.reason === 'signal is aborted without reason' ||
        e.message?.includes('aborted') ||
        e.reason?.message?.includes('aborted');

      if (isAbort) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener('unhandledrejection', handleAbortError);
    window.addEventListener('error', handleAbortError);
    return () => {
      window.removeEventListener('unhandledrejection', handleAbortError);
      window.removeEventListener('error', handleAbortError);
    };
  }, []);

  return (
    <Routes>
      <Route path="/" element={<LandingPage isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />} />
      <Route path="/login" element={<LoginPage isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />} />
      <Route path="/signup" element={<SignupPage isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />} />
      <Route path="/verify-otp" element={<OTPVerification isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />} />
      <Route path="/pricing" element={<PricingPage isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />} />
      <Route path="/success" element={<SuccessPage isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />} />
      <Route path="/forgot-password" element={<ForgotPassword isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />} />
      <Route path="/reset-password" element={<ResetPasswordPage isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />} />
      <Route path="/reset-password/" element={<ResetPasswordPage isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />} />
      <Route path="/restricted" element={<RestrictedAccess isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <MainAppContent isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}